import { computed, reactive } from 'vue'
import { getMyInfo, login as apiLogin, register as apiRegister, logout as apiLogout } from '../services/api'

// Dies ist der globale, reaktive Zustand für die Authentifizierung.
// Da es außerhalb der `useAuth`-Funktion definiert ist, wird es in der gesamten
// Anwendung geteilt (Singleton-Pattern).
const state = reactive({user: null, ready: false, checking: false})

// Verhindert parallele `getMyInfo`-Aufrufe. Wenn bereits eine Aktualisierung läuft,
// wird das existierende Promise zurückgegeben, anstatt ein neues zu starten.
let refreshPromise = null

/**
 * Ein Vue Composable zur Verwaltung des globalen Authentifizierungszustands.
 * Stellt reaktive Daten und Methoden zur Verfügung, um den Benutzerstatus
 * in der gesamten Anwendung zu verwalten.
 */
export function useAuth() {
  /**
   * Aktualisiert den Benutzerstatus durch einen API-Aufruf an /users/me.
   * @param {object} options - Optionen, z.B. { force: true }, um einen neuen API-Aufruf zu erzwingen.
   */
  const refreshAuth = async ({ force = false } = {}) => {
    if (state.checking && !force) return refreshPromise
    state.checking = true
    refreshPromise = (async () => {
      try {
        const res = await getMyInfo()
          if (res?.success === false) {
          state.user = null
          return null
        }
        state.user = res?.answer ?? null
        return state.user
      } catch (e) {
        state.user = null
        return null
      } finally {
        state.ready = true
        state.checking = false
      }
    })()
    return refreshPromise
  }

  /**
   * Führt einen Login durch und aktualisiert anschließend den globalen Benutzerstatus.
   * @param {string} email
   * @param {string} password
   */
  const login = async (email, password) => {
    const res = await apiLogin(email, password)
    await refreshAuth({ force: true })
    return res
  }

  /**
   * Führt eine Registrierung durch und aktualisiert anschließend den globalen Benutzerstatus.
   * @param {string} email
   * @param {string} password
   * @param {string|null} code
   */
  const register = async (email, password, code = null) => {
    const res = await apiRegister(email, password, code)
    await refreshAuth({ force: true })
    return res
  }

  /**
   * Führt einen Logout durch und setzt den lokalen Benutzerstatus zurück.
   */
  const logout = async () => {
    try {
      await apiLogout()
    } finally {
      state.user = null
    }
  }

  /**
   * Ermöglicht das manuelle Setzen des Benutzerobjekts (nützlich für Tests).
   * @param {object|null} user
   */
  const setUser = (user) => {
    state.user = user
    state.ready = true
  }

  return {
    user: computed(() => state.user),
    // Ist ein Benutzer authentifiziert?
    isAuthenticated: computed(() => !!state.user),
    // Ist der initiale Auth-Check abgeschlossen?
    ready: computed(() => state.ready),
    // Läuft gerade ein Auth-Check?
    checking: computed(() => state.checking),
    // Methoden zum Interagieren mit dem Auth-Status.
    refreshAuth,
    login,
    register,
    logout,
    setUser,
  }
}